import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import Button from '../ui/Button';
import ErrorCard from '../ui/ErrorCard';
import Modal from '../ui/Modal';
import { Id, Mono } from '../ui/Mono';
import Select from '../ui/Select';
import { SkeletonTable } from '../ui/Skeleton';
import StandardTable from '../ui/StandardTable';
import { useToast } from '../ui/Toast';
import { ExtractListRows } from './payload';
import { useWarehouseLabels } from './warehouseLabels';

// Enum FECHADO do contrato do M2 (ordem canônica do PRD §6.3.2/§11). A tabela
// mostra SEMPRE as 5 linhas — regra não configurada é estado normal (nunca foi
// definida), não ausência de dado; por isso as linhas nascem daqui e o GET só
// preenche o destino.
const DEPOSIT_RULE_EVENT_KEYS = [
    'PRODUCTION_OUTPUT',
    'FACTORY_IN_ENTRY',
    'INCIDENT_ENTRY',
    'CUSTOMER_RETURN_RECEIPT',
    'RMA_SEND',
];

/**
 * Aba "Regras de depósito" (M2): evento tipado → depósito destino. Quando o
 * evento acontecer (motor do M4/M5), o LPN é transferido AUTOMATICAMENTE para
 * o depósito configurado — transferência CONTÁBIL (DEPOSIT_TRANSFER); depósito
 * nunca bloqueia posição física (§6.3). SEM DELETE: trocar = editar.
 */
const DepositRulesManager = () => {
    const toast = useToast();
    const { label } = useWarehouseLabels();
    const [rulesByEvent, setRulesByEvent] = useState({});
    const [activeDeposits, setActiveDeposits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    // Modal de edição: a linha inteira (event_key + destino atual) para o
    // título e para oferecer o depósito atual mesmo se ele foi desativado.
    const [editingRow, setEditingRow] = useState(null);
    const [selectedDepositId, setSelectedDepositId] = useState('');
    const [formError, setFormError] = useState(null);
    const [saving, setSaving] = useState(false);

    // Regras + depósitos ATIVOS (opções do select) carregam juntos: vêm do
    // mesmo backend/permissão, e a aba não funciona sem os dois.
    const loadData = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const [rulesData, depositsData] = await Promise.all([
                api.getWarehouseDepositRules(),
                api.getWarehouseDeposits({ active: 'true', limit: 500 }),
            ]);
            const byEvent = {};
            for (const rule of ExtractListRows(rulesData, ['results'])) {
                if (rule?.event_key) {
                    byEvent[rule.event_key] = rule;
                }
            }
            setRulesByEvent(byEvent);
            setActiveDeposits(ExtractListRows(depositsData, ['deposits']));
        } catch (error) {
            setLoadError(error?.message || 'Erro ao carregar');
            setRulesByEvent({});
            setActiveDeposits([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const rows = useMemo(
        () =>
            DEPOSIT_RULE_EVENT_KEYS.map((eventKey) => {
                const rule = rulesByEvent[eventKey];
                return {
                    event_key: eventKey,
                    deposit_id: rule?.deposit_id ?? null,
                    deposit_code: rule?.deposit_code ?? null,
                    deposit_label: rule?.deposit_label ?? null,
                };
            }),
        [rulesByEvent],
    );

    const openEdit = useCallback((row) => {
        setEditingRow(row);
        setSelectedDepositId(row.deposit_id ?? '');
        setFormError(null);
    }, []);

    const closeModal = () => {
        if (!saving) {
            setEditingRow(null);
        }
    };

    // Opções do select: depósitos ATIVOS; se o destino atual foi desativado
    // depois de configurado, ele entra marcado "(inativo)" — sumir com o valor
    // gravado deixaria o select mentindo (placeholder com regra configurada).
    const depositOptions = useMemo(() => {
        const options = activeDeposits.map((deposit) => ({
            value: deposit.id,
            label: `${deposit.code} — ${deposit.label}`,
        }));
        if (editingRow?.deposit_id && !options.some((option) => option.value === editingRow.deposit_id)) {
            options.push({
                value: editingRow.deposit_id,
                label: `${editingRow.deposit_code} — ${editingRow.deposit_label} ${label('wms.deposit_rules.deposit_inactive_suffix')}`,
            });
        }
        return options;
    }, [activeDeposits, editingRow, label]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!selectedDepositId) {
            setFormError(label('wms.common.required'));
            return;
        }
        setSaving(true);
        setFormError(null);
        try {
            await api.putWarehouseDepositRule(editingRow.event_key, selectedDepositId);
            toast.success(label('wms.deposit_rules.saved'));
            setEditingRow(null);
            await loadData();
        } catch (error) {
            if (error?.code === 'NETWORK_ERROR') {
                setFormError(label('wms.collector.error_network'));
            } else {
                setFormError(error?.message || 'Erro ao salvar');
            }
        } finally {
            setSaving(false);
        }
    };

    // Colunas sem sort: as 5 linhas ficam na ordem canônica do enum.
    const columns = useMemo(
        () => [
            {
                key: 'event',
                label: label('wms.deposit_rules.event'),
                sortable: false,
                getValue: (row) => row.event_key,
                render: (row) => (
                    <div>
                        <span className="block font-medium text-black dark:text-white">
                            {label(`wms.deposit_event.${row.event_key}`)}
                        </span>
                        <Mono className="text-xs text-mono-400">{row.event_key}</Mono>
                    </div>
                ),
            },
            {
                key: 'deposit',
                label: label('wms.deposit_rules.target'),
                sortable: false,
                getValue: (row) => (row.deposit_id ? `${row.deposit_code} ${row.deposit_label}` : ''),
                render: (row) =>
                    row.deposit_id ? (
                        <span className="inline-flex flex-wrap items-baseline gap-x-1.5">
                            <Id value={row.deposit_code} className="font-medium" />
                            <span>{row.deposit_label}</span>
                        </span>
                    ) : (
                        <span className="text-mono-400">{label('wms.deposit_rules.not_configured')}</span>
                    ),
            },
            {
                key: 'actions',
                label: label('wms.common.actions'),
                sortable: false,
                getValue: () => '',
                render: (row) => (
                    <Button variant="ghost" size="sm" onClick={() => openEdit(row)}>
                        {label('wms.common.edit')}
                    </Button>
                ),
            },
        ],
        [label, openEdit],
    );

    return (
        <section className="space-y-4">
            <div className="space-y-1">
                <h2 className="text-lg font-semibold text-black dark:text-white">
                    {label('wms.deposit_rules.title')}
                </h2>
                <p className="max-w-3xl text-sm text-mono-600 dark:text-mono-400">
                    {label('wms.deposit_rules.description')}
                </p>
            </div>

            {loading ? (
                <SkeletonTable columns={3} rows={5} />
            ) : loadError ? (
                <ErrorCard
                    title={label('wms.deposit_rules.title')}
                    message={`${label('wms.common.load_error')}\n${loadError}`}
                    action={label('wms.common.retry')}
                    actionFn={loadData}
                />
            ) : (
                // 5 linhas fixas: sem busca/export/paginação (variant minimal).
                <StandardTable
                    variant="minimal"
                    rows={rows}
                    columns={columns}
                    rowKey={(row) => row.event_key}
                />
            )}

            <Modal
                isOpen={Boolean(editingRow)}
                onClose={closeModal}
                title={
                    editingRow
                        ? `${label('wms.common.edit')} — ${label(`wms.deposit_event.${editingRow.event_key}`)}`
                        : ''
                }
                size="md"
            >
                <form onSubmit={handleSubmit} className="space-y-4">
                    {editingRow ? (
                        <p className="text-sm text-mono-600 dark:text-mono-400">
                            {label(`wms.deposit_event.${editingRow.event_key}`)}{' '}
                            <Mono className="text-xs text-mono-400">{editingRow.event_key}</Mono>
                        </p>
                    ) : null}
                    <div>
                        <Select
                            label={label('wms.deposit_rules.target')}
                            value={selectedDepositId}
                            onChange={(event) => setSelectedDepositId(event.target.value)}
                            errorMessage={formError}
                            disabled={saving}
                        >
                            <option value="">{label('wms.deposit_rules.select_deposit')}</option>
                            {depositOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </Select>
                        {!formError ? (
                            <p className="mt-1 text-xs text-mono-500 dark:text-mono-400">
                                {depositOptions.length === 0
                                    ? label('wms.deposit_rules.no_deposits')
                                    : label('wms.deposit_rules.deposit_help')}
                            </p>
                        ) : null}
                    </div>

                    <div className="flex flex-col gap-3 pt-4 sm:flex-row">
                        <Button type="submit" loading={saving} className="flex-1">
                            {label('wms.common.save')}
                        </Button>
                        <Button type="button" onClick={closeModal} variant="secondary" className="sm:w-auto">
                            {label('wms.common.cancel')}
                        </Button>
                    </div>
                </form>
            </Modal>
        </section>
    );
};

export default DepositRulesManager;
